import { analyticsConfig } from '../config/analytics';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
}

export function useAnalytics() {
  const isDataLayerAvailable = () => {
    return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
  };

  const waitForDataLayer = (): Promise<void> => {
    return new Promise((resolve) => {
      if (isDataLayerAvailable()) {
        resolve();
        return;
      }

      // Wait for dataLayer to be available (max 5 seconds)
      let attempts = 0;
      const maxAttempts = 50; // 50 * 100ms = 5 seconds
      
      const checkDataLayer = () => {
        attempts++;
        if (isDataLayerAvailable()) {
          resolve();
        } else if (attempts < maxAttempts) {
          setTimeout(checkDataLayer, 100);
        } else {
          console.warn('DataLayer not available after 5 seconds');
          resolve();
        }
      };
      
      checkDataLayer();
    });
  };

  const trackPageView = async (pagePath: string, pageTitle?: string) => {
    if (!analyticsConfig.enabled) {
      if (analyticsConfig.debug) {
        console.log('Analytics disabled, skipping page view tracking');
      }
      return;
    }

    await waitForDataLayer();
    
    if (!isDataLayerAvailable()) {
      console.warn('DataLayer not available for page view tracking');
      return;
    }

    if (analyticsConfig.debug) {
      console.log('Tracking page view:', pagePath, pageTitle);
    }

    try {
      // Push to GTM dataLayer
      window.dataLayer.push({
        event: 'page_view',
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath
      });

      if (analyticsConfig.debug) {
        console.log('Page view tracked successfully:', pagePath, pageTitle);
        console.log('Current dataLayer:', window.dataLayer);
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  const trackEvent = async (event: AnalyticsEvent) => {
    if (!analyticsConfig.enabled) {
      if (analyticsConfig.debug) {
        console.log('Analytics disabled, skipping event tracking');
      }
      return;
    }

    await waitForDataLayer();
    
    if (!isDataLayerAvailable()) {
      console.warn('DataLayer not available for event tracking');
      return;
    }

    if (analyticsConfig.debug) {
      console.log('Tracking event:', event);
    }

    try {
      // Push to GTM dataLayer
      window.dataLayer.push({
        event: event.action,
        category: event.category,
        label: event.label,
        value: event.value,
        // Add custom parameters for better tracking
        custom_parameter_1: event.category,
        custom_parameter_2: event.label,
      });

      if (analyticsConfig.debug) {
        console.log('Event tracked successfully:', event);
        console.log('Current dataLayer:', window.dataLayer);
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };

  const trackConversion = async (conversionId: string, value?: number) => {
    if (!analyticsConfig.enabled) return;
    
    await waitForDataLayer();
    
    if (!isDataLayerAvailable()) {
      console.warn('DataLayer not available for conversion tracking');
      return;
    }

    try {
      window.dataLayer.push({
        event: 'conversion',
        send_to: conversionId,
        value: value,
      });
    } catch (error) {
      console.error('Error tracking conversion:', error);
    }
  };

  const trackEngagement = async (engagementType: string, details?: any) => {
    if (!analyticsConfig.enabled) return;
    
    await waitForDataLayer();
    
    if (!isDataLayerAvailable()) {
      console.warn('DataLayer not available for engagement tracking');
      return;
    }

    try {
      window.dataLayer.push({
        event: 'engagement',
        engagement_type: engagementType,
        ...details,
      });
    } catch (error) {
      console.error('Error tracking engagement:', error);
    }
  };

  const trackButtonClick = async (buttonName: string, location?: string) => {
    await trackEvent({
      action: 'click',
      category: 'button',
      label: `${buttonName}${location ? ` - ${location}` : ''}`,
    });
  };

  const trackFormSubmission = async (formName: string, success: boolean = true) => {
    await trackEvent({
      action: success ? 'submit_success' : 'submit_error',
      category: 'form',
      label: formName,
    });
  };

  const trackFeatureUsage = async (featureName: string) => {
    await trackEvent({
      action: 'feature_use',
      category: 'engagement',
      label: featureName,
    });
  };

  const trackDownload = async (fileName: string, fileType?: string) => {
    await trackEvent({
      action: 'download',
      category: 'file',
      label: `${fileName}${fileType ? ` (${fileType})` : ''}`,
    });
  };

  return {
    trackPageView,
    trackEvent,
    trackConversion,
    trackEngagement,
    trackButtonClick,
    trackFormSubmission,
    trackFeatureUsage,
    trackDownload,
  };
} 